import React, { useState, useEffect, Suspense, lazy } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import host from "../../global.jsx";
import { getWhiteLabelFromJobLink } from "../../utils/whitelabel";
import PublicJobLayout from "./PublicJobLayout";
import PublicJobApplication from "./PublicJobApplication";

const FORM_REGISTRY = {
    k4: lazy(() => import("./ATSForms/k4")),
    // Add future forms here:
    // k5: lazy(() => import("./ATSForms/k5")),
    // dubai_form: lazy(() => import("./ATSForms/dubai_form")),
};

const ATSFormDispatcher = () => {
    const { linkToken } = useParams();
    const [jobDetails, setJobDetails] = useState(null);
    const [branding, setBranding] = useState(null);
    const [organizationName, setOrganizationName] = useState("Loading...");
    const [isLoading, setIsLoading] = useState(true);
    const [applicationSubmitted, setApplicationSubmitted] = useState(false);
    const [applicationClosed, setApplicationClosed] = useState(false);
    const [isWhiteLabelingEnable, setisWhiteLabelingEnable] = useState(false);
    const [atsFormName, setAtsFormName] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Branding
                const brandingConfig = await getWhiteLabelFromJobLink(linkToken);
                setBranding(brandingConfig);
                setisWhiteLabelingEnable(brandingConfig.whitelabelEnabled);

                // Fetch Job Details
                const response = await axios.get(`${host}/public/job-details/${linkToken}`);
                setJobDetails(response.data);
                setAtsFormName(response.data.ats_form_name || null);

                // Fetch Organization Name if not in response
                if (response.data.organization_name) {
                    setOrganizationName(response.data.organization_name);
                } else {
                    const adminId = response.data.admin_id || response.data.created_by || response.data.org_admin_id;
                    if (adminId) {
                        const orgRes = await axios.get(`${host}/get-organization-by-admin/${adminId}`);
                        setOrganizationName(orgRes.data.OrganizationName || "Company");
                    } else {
                        setOrganizationName("Company");
                    }
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                if (error.response?.status === 404) {
                    if (error.response?.data?.detail?.includes("no longer accepting applications")) {
                        setApplicationClosed(true);
                    }
                }
                setIsLoading(false);
            }
        };

        fetchData();
    }, [linkToken]);

    // While still loading, show a spinner to avoid flashing the wrong form
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center app-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
        );
    }

    // Look up the form component from the registry
    const FormComponent = atsFormName ? FORM_REGISTRY[atsFormName] : null;

    // If a matching custom form exists in the registry, render it inside PublicJobLayout
    if (FormComponent) {
        return (
            <PublicJobLayout
                jobDetails={jobDetails}
                organizationName={organizationName}
                branding={branding}
                isWhiteLabelingEnable={isWhiteLabelingEnable}
                isLoading={isLoading}
                applicationClosed={applicationClosed}
                applicationSubmitted={applicationSubmitted}
            >
                <Suspense
                    fallback={
                        <div className="flex items-center justify-center p-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    }
                >
                    <FormComponent
                        linkToken={linkToken}
                        jobDetails={jobDetails}
                        branding={branding}
                        isWhiteLabelingEnable={isWhiteLabelingEnable}
                        host={host}
                        setApplicationSubmitted={setApplicationSubmitted}
                        setApplicationClosed={setApplicationClosed}
                    />
                </Suspense>
            </PublicJobLayout>
        );
    }

    // No matching form in registry → fall back to PublicJobApplication
    return <PublicJobApplication />;
};

export default ATSFormDispatcher;
